// V14.13.11 — SIP Call Logs viewer (Phase 4)
//
// Read-only table of SIP calls (admins see everything; users see their own).
// Filters: window in days, exercise, extension.
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { PhoneCall, RefreshCw, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import { toast } from 'sonner';
import ConnectionsSubNav from '../../components/hub/ConnectionsSubNav';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fetchJson = async (url) => {
  const token = localStorage.getItem('ems_token');
  const r = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
  return r.json();
};

const fmtMs = (ms) => {
  if (!ms) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
};
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString() : '—';

const SipCallLogsPage = () => {
  const [days, setDays] = useState('7');
  const [exerciseFilter, setExerciseFilter] = useState('');
  const [extensions, setExtensions] = useState({});
  const [exercises, setExercises] = useState({});
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_direction: [], by_extension: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ days, limit: '500' });
      if (exerciseFilter) qs.append('exercise_id', exerciseFilter);
      const [list, sum, exs, exers] = await Promise.all([
        fetchJson(`${API_URL}/hub/sip-call-logs?${qs}`),
        fetchJson(`${API_URL}/hub/sip-call-logs/summary?${qs}`).catch(() => ({ total: 0, by_direction: [], by_extension: [] })),
        fetchJson(`${API_URL}/hub/sip-extensions`).catch(() => []),
        fetchJson(`${API_URL}/exercises`).catch(() => []),
      ]);
      setLogs(list || []);
      setSummary(sum);
      setExtensions(Object.fromEntries((exs || []).map(e => [e.extension_id, e.name])));
      const exList = Array.isArray(exers) ? exers : (exers?.exercises || []);
      setExercises(Object.fromEntries(exList.map(e => [e.exercise_id, e.name])));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [days, exerciseFilter]);

  useEffect(() => { load(); }, [load]);

  const totalSeconds = summary.by_direction?.reduce((acc, r) => acc + (r.total_ms || 0), 0) / 1000;
  const inboundN = summary.by_direction?.find(d => d.direction === 'inbound')?.count || 0;
  const outboundN = summary.by_direction?.find(d => d.direction === 'outbound')?.count || 0;

  return (
    <div data-testid="sip-call-logs-page">
      <ConnectionsSubNav />
      <div className="p-6 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <PhoneCall className="w-7 h-7" /> SIP Call Logs
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Every call placed/received through the Hub softphone. Admins see all users, users see their own.
            </p>
          </div>
          <div className="flex gap-2 items-end">
            <div className="space-y-1">
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Window</label>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="h-9 w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">24 h</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-[11px] uppercase tracking-wider text-muted-foreground">Exercise</label>
              <Select value={exerciseFilter || 'all'} onValueChange={(v) => setExerciseFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-9 w-56"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All exercises</SelectItem>
                  {Object.entries(exercises).map(([id, name]) => (
                    <SelectItem key={id} value={id}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="sip-logs-refresh">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="pt-6"><div className="text-3xl font-bold">{summary.total}</div><div className="text-xs text-muted-foreground mt-1">Total calls</div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-3xl font-bold text-emerald-600">{inboundN}</div><div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><PhoneIncoming className="w-3 h-3" />Inbound</div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-3xl font-bold text-blue-600">{outboundN}</div><div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><PhoneOutgoing className="w-3 h-3" />Outbound</div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-3xl font-bold">{Math.round(totalSeconds / 60)}m</div><div className="text-xs text-muted-foreground mt-1">Talk time</div></CardContent></Card>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sip-logs-table">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">When</th>
                    <th className="text-left px-4 py-2">Dir</th>
                    <th className="text-left px-4 py-2">User</th>
                    <th className="text-left px-4 py-2">Extension</th>
                    <th className="text-left px-4 py-2">Remote</th>
                    <th className="text-left px-4 py-2">Exercise</th>
                    <th className="text-left px-4 py-2">Duration</th>
                    <th className="text-left px-4 py-2">End</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {logs.length === 0 && (
                    <tr><td colSpan={8} className="text-center text-muted-foreground py-8">No calls in this window.</td></tr>
                  )}
                  {logs.map((l) => (
                    <tr key={l.log_id} className="hover:bg-muted/30">
                      <td className="px-4 py-2 whitespace-nowrap text-xs">{fmtTime(l.started_at)}</td>
                      <td className="px-4 py-2">
                        <Badge variant="outline" className="text-[10px] uppercase gap-1">
                          {l.direction === 'inbound' ? <PhoneIncoming className="w-3 h-3" /> : <PhoneOutgoing className="w-3 h-3" />}
                          {l.direction}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 truncate max-w-[160px]">{l.user_name || l.user_id}</td>
                      <td className="px-4 py-2"><code className="text-xs">{extensions[l.extension_id] || l.extension_id}</code></td>
                      <td className="px-4 py-2"><code className="text-xs">{l.remote_uri}</code></td>
                      <td className="px-4 py-2 text-xs">{exercises[l.exercise_id] || l.exercise_id || '—'}</td>
                      <td className="px-4 py-2 whitespace-nowrap font-mono text-xs">{fmtMs(l.duration_ms)}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{l.end_reason || '—'}{l.failed_code ? ` (${l.failed_code})` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SipCallLogsPage;
